import { HStack, Modal, VStack } from '@musinsa/mwms-ds'
import { BulkKey } from './DeliveryAreasTableTitle'
import { RefObject } from 'react'
import { AgGridReact } from 'ag-grid-react'
import Text from '@/components/content/Text'
import { colorSystem } from '@/styles/theme'
import { DeliveryAreaItem, UpdateDeliveryAreaReq } from '@/services/deliveryAreas/types'
import RadioBool from '@/components/content/RadioBool'
import Input from '@/components/content/Input'
import { SelectBoxCarrier } from './SelectBoxCarrier'
import { useForm } from 'react-hook-form'
import { inRange, toNumber } from 'lodash-es'
import { useUpdateDeliveryArea } from '@/services/deliveryAreas/useDeliveryAreasQueries'
import MToast from '@/utils/modal/MToast'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import { FormField } from '../../../../components/content/FormField'
import styled from 'styled-components'
import MRender from '@/utils/modal/MRender'
import Button from '@/components/content/Button'
import { ConfirmModal } from '@/components/content/ConfirmModal'

type Props = {
  $grid: RefObject<AgGridReact<DeliveryAreaItem>>
  bulkKey: BulkKey
  onRefetch: () => void
  onClose: () => void
}

const labelMap: Record<BulkKey, string> = {
  deliveryLeadDay: '리드타임',
  remoteArea: '도서산간여부',
  isHolidayDeliveryAvailable: '휴일배송여부',
  defaultCarrier: '주중 운송사',
  holidayCarrier: '휴일 운송사',
}

const getJosa = (word: string) => {
  const lastChar = word.charAt(word.length - 1)
  const hasJongseong = (lastChar.charCodeAt(0) - 0xac00) % 28 > 0

  return hasJongseong ? '을' : '를'
}

export function BulkUpdateModal({ $grid, bulkKey, onRefetch, onClose }: Props) {
  const selectedRows = $grid.current?.api.getSelectedRows() ?? []

  const { setValue, watch } = useForm<Pick<UpdateDeliveryAreaReq, BulkKey>>()
  const formValues = watch()

  const submitDisabled = formValues[bulkKey] === undefined

  const field = (() => {
    // eslint-disable-next-line default-case
    switch (bulkKey) {
      case 'deliveryLeadDay':
        return (
          <Input
            value={formValues.deliveryLeadDay}
            placeholder="변경할 리드타입을 입력해 주세요."
            onChange={(v) => {
              setValue('deliveryLeadDay', toNumber(v))
            }}
          />
        )

      case 'remoteArea':
        return <RadioBool value={formValues.remoteArea} onChange={(v) => setValue('remoteArea', v)} />

      case 'isHolidayDeliveryAvailable':
        return <RadioBool value={formValues.isHolidayDeliveryAvailable} onChange={(v) => setValue('isHolidayDeliveryAvailable', v)} />

      case 'defaultCarrier':
        return <SelectBoxCarrier value={formValues.defaultCarrier} onChange={(v) => setValue('defaultCarrier', v)} />

      case 'holidayCarrier':
        return <SelectBoxCarrier value={formValues.holidayCarrier} onChange={(v) => setValue('holidayCarrier', v)} />
    }
  })()

  const updateDeliveryArea = useUpdateDeliveryArea()

  const submitChangeBulk = (payload: UpdateDeliveryAreaReq[]) => {
    updateDeliveryArea
      .mutateAsync(payload)
      .then(async () => {
        await onRefetch()

        $grid.current?.api?.deselectAll()
        MToast.show(`${selectedRows.length.toLocaleString()}개 수정 완료`)
        onClose()
      })
      .catch((e) => {
        MAlert.show(Utils.getApiErrMessage(e, '일괄 변경에 실패했습니다.'))
      })
  }

  /** 일괄변경 submit -> 케이스에 따라 confirm 노출 */
  const handleSubmitClick = () => {
    const { deliveryLeadDay, remoteArea } = formValues

    if (bulkKey === 'deliveryLeadDay') {
      if (deliveryLeadDay > 9) {
        MRender.show(<DeliveryLeadDayInfoModal onClose={() => MRender.destory()} />)
        return
      }

      const hasRemoteAreaN = selectedRows.some((row) => !row.remoteArea)

      const isChangeError = (deliveryLeadDay === 0 || deliveryLeadDay >= 4) && hasRemoteAreaN

      if (isChangeError) {
        MRender.show(
          <DeliveryLeadDayConfirmModal
            deliveryLeadDay={deliveryLeadDay}
            isBulkUpdate
            onClose={() => MRender.destory()}
            onConfirm={() => {
              submitChangeBulk(selectedRows.map((row) => ({ ...row, deliveryLeadDay, remoteArea: true })) as UpdateDeliveryAreaReq[])
              MRender.destory()
              onClose()
            }}
          />,
        )
        return
      }
    }

    if (bulkKey === 'remoteArea') {
      const isAllLeadDay1to3 = selectedRows.every((row) => inRange(row.deliveryLeadDay, 1, 4))

      if (!remoteArea && !isAllLeadDay1to3) {
        MRender.show(
          <RemoteAreaConfirmModal
            onConfirm={() => {
              MRender.destory()
              onClose()
            }}
          />,
        )
        return
      }
    }

    submitChangeBulk(selectedRows.map((row) => ({ ...row, [bulkKey]: formValues[bulkKey] })) as UpdateDeliveryAreaReq[])
  }

  return (
    <Modal
      title={`${labelMap[bulkKey]} 일괄변경`}
      open
      onClose={onClose}
      size="sm"
      buttons={[
        { kind: 'secondary', content: '취소', onClick: onClose },
        { kind: 'primary_light', content: '변경', onClick: handleSubmitClick, disabled: submitDisabled },
      ]}
    >
      <VStack gap={16}>
        <Text kind="Body_14_regular">
          선택하신&nbsp;
          <Text kind="Body_14_bold" color={colorSystem.text.common.success}>
            {selectedRows.length.toLocaleString()}
          </Text>{' '}
          건의 {labelMap[bulkKey]}
          {getJosa(labelMap[bulkKey])} 일괄 변경합니다.
        </Text>
        <FormField label={labelMap[bulkKey]}>{field}</FormField>
      </VStack>
    </Modal>
  )
}

export function DeliveryLeadDayInfoModal({ onClose }: { onClose: () => void }) {
  return (
    <ConfirmModal
      title="리드타임은 9이하로 입력해 주세요."
      footer={
        <Button fit kind="primary_light" onClick={onClose}>
          확인
        </Button>
      }
    />
  )
}

export function DeliveryLeadDayConfirmModal({
  deliveryLeadDay,
  isBulkUpdate = false,
  onClose,
  onConfirm,
}: {
  deliveryLeadDay: number
  isBulkUpdate?: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  const isZeroDay = deliveryLeadDay === 0

  const title = `리드타임 ${isZeroDay ? '0일로' : '4일 이상으로'} 변경 시,\n도서산간 여부는 ${isBulkUpdate ? '모두' : ''} 'Y'로 자동 변경됩니다.\n이대로 변경하시겠어요?`
  const description = isBulkUpdate ? `도서산간 여부가 'N'인 우편번호가 모두 'Y'로 일괄 변경됩니다.` : undefined

  return (
    <ConfirmModal
      title={title}
      description={description}
      footer={
        <HStack gap={8}>
          <Button fit kind="secondary" type="button" onClick={onClose}>
            취소
          </Button>
          <Button fit kind="primary_light" type="button" onClick={onConfirm}>
            변경
          </Button>
        </HStack>
      }
    />
  )
}

export function RemoteAreaConfirmModal({ onConfirm }: { onConfirm: () => void }) {
  return (
    <ConfirmModal
      title={`리드타임이 0 또는 4 이상인 우편번호가 포함되어,\n도서산간여부를 'N'으로 변경할 수 없어요.`}
      description="먼저 해당 우편번호의 리드타임을 1~3 사이로 수정해 주세요."
      footer={
        <Button fit kind="primary_light" onClick={onConfirm}>
          확인
        </Button>
      }
    />
  )
}
